import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  TextField,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  FormControlLabel,
  Checkbox,
} from '@mui/material'
import Edit from '@mui/icons-material/Edit'
import Save from '@mui/icons-material/Save'
import Cancel from '@mui/icons-material/Cancel'
import Email from '@mui/icons-material/Email'
import Phone from '@mui/icons-material/Phone'
import WhatsApp from '@mui/icons-material/WhatsApp'
import Sms from '@mui/icons-material/Sms'
import Person from '@mui/icons-material/Person'
import Business from '@mui/icons-material/Business'
import { styled } from '@mui/material/styles'
import { getSupportAgentProfile, updateSupportAgentProfile } from '../../services/supportAgentProfileService'
import { toast } from 'react-toastify'

const ProfileContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  maxWidth: '100%',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}))

const ProfileHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
  paddingBottom: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}))

const InfoRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
}))

const InfoLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  minWidth: '140px',
  color: theme.palette.text.secondary,
  fontSize: '14px',
  [theme.breakpoints.down('sm')]: {
    minWidth: 'auto',
  },
}))

const InfoValue = styled(Typography)(({ theme }) => ({
  flex: 1,
  color: theme.palette.text.primary,
  fontSize: '14px',
  wordBreak: 'break-word',
  display: 'block',
}))

const ModeChip = styled(Chip)(({ theme }) => ({
  marginRight: theme.spacing(0.5),
  marginBottom: theme.spacing(0.5),
}))

const SUPPORT_MODES = [
  { value: 'whatsapp', label: 'WhatsApp', icon: WhatsApp },
  { value: 'email', label: 'Email', icon: Email },
  { value: 'sms', label: 'SMS', icon: Sms },
  { value: 'phone', label: 'Phone', icon: Phone },
]

export default function SupportAgentProfile({ onClose }) {
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [formData, setFormData] = useState({
    screenName: '',
    supportEmail: '',
    supportPhoneNumber: '',
    supportMode: [],
  })

  useEffect(() => {
    fetchProfile()
  }, [])

  const fetchProfile = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getSupportAgentProfile()
      if (data) {
        setProfile(data)
        setFormData({
          screenName: data.screenName || '',
          supportEmail: data.supportEmail || '',
          supportPhoneNumber: data.supportPhoneNumber || '',
          supportMode: data.supportMode || [],
        })
      } else {
        setError('Failed to load profile. Please try again.')
      }
    } catch (err) {
      if (err.message === 'INVALID_USER_ID') {
        setError('You do not have a Support Agent profile. Please contact your administrator.')
      } else {
        setError('An error occurred while loading your profile.')
      }
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = () => {
    setEditing(true)
    setError(null)
  }

  const handleCancel = () => {
    setEditing(false)
    setError(null)
    // Reset form data to original profile
    if (profile) {
      setFormData({
        screenName: profile.screenName || '',
        supportEmail: profile.supportEmail || '',
        supportPhoneNumber: profile.supportPhoneNumber || '',
        supportMode: profile.supportMode || [],
      })
    }
  }

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      const updatedProfile = await updateSupportAgentProfile(formData)
      if (updatedProfile) {
        setProfile(updatedProfile)
        setEditing(false)
        toast.success('Profile updated successfully!')
      } else {
        setError('Failed to update profile. Please try again.')
      }
    } catch (err) {
      setError('An error occurred while updating your profile.')
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  const handleModeToggle = (mode) => {
    setFormData((prev) => {
      const currentModes = prev.supportMode || []
      const isSelected = currentModes.includes(mode)
      return {
        ...prev,
        supportMode: isSelected
          ? currentModes.filter((m) => m !== mode)
          : [...currentModes, mode],
      }
    })
  }

  if (loading) {
    return (
      <ProfileContainer>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </ProfileContainer>
    )
  }

  if (error && !profile) {
    return (
      <ProfileContainer>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={fetchProfile}>
          Retry
        </Button>
      </ProfileContainer>
    )
  }

  return (
    <ProfileContainer>
      <ProfileHeader>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            backgroundColor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
          }}
        >
          <Person sx={{ fontSize: 32 }} />
        </Box>
        <Box flex={1}>
          <Typography variant="h5" fontWeight={600}>
            Support Agent Profile
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {profile?.escalationLevel || 'N/A'} Support Level
          </Typography>
        </Box>
        {!editing ? (
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={handleEdit}
            sx={{
              background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.95) 0%, rgba(99, 102, 241, 0.95) 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, rgba(67, 56, 202, 1) 0%, rgba(99, 102, 241, 1) 100%)',
              },
            }}
          >
            Edit
          </Button>
        ) : (
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              startIcon={<Cancel />}
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={handleSave}
              disabled={saving}
              sx={{
                background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.95) 0%, rgba(99, 102, 241, 0.95) 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, rgba(67, 56, 202, 1) 0%, rgba(99, 102, 241, 1) 100%)',
                },
              }}
            >
              {saving ? <CircularProgress size={20} /> : 'Save'}
            </Button>
          </Box>
        )}
      </ProfileHeader>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box>
        {/* Read-only fields */}
        <InfoRow>
          <InfoLabel>User ID</InfoLabel>
          <InfoValue component="div">{profile?.userId || '--'}</InfoValue>
        </InfoRow>

        <InfoRow>
          <InfoLabel>Escalation Level</InfoLabel>
          <InfoValue component="div">
            <Chip
              label={profile?.escalationLevel || 'N/A'}
              color="primary"
              size="small"
              sx={{ fontWeight: 600 }}
            />
          </InfoValue>
        </InfoRow>

        <InfoRow>
          <InfoLabel>Open to All</InfoLabel>
          <InfoValue component="div">
            <Chip
              label={profile?.openToAll ? 'Yes' : 'No'}
              color={profile?.openToAll ? 'success' : 'default'}
              size="small"
            />
          </InfoValue>
        </InfoRow>

        {profile?.affiliations && profile.affiliations.length > 0 && (
          <InfoRow>
            <InfoLabel>Affiliations</InfoLabel>
            <InfoValue component="div">
              <Box display="flex" flexWrap="wrap" gap={0.5}>
                {profile.affiliations.map((affiliation, index) => (
                  <Chip key={index} label={affiliation} size="small" variant="outlined" />
                ))}
              </Box>
            </InfoValue>
          </InfoRow>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Editable fields */}
        {editing ? (
          <>
            <TextField
              fullWidth
              label="Screen Name"
              value={formData.screenName}
              onChange={(e) => setFormData({ ...formData, screenName: e.target.value })}
              margin="normal"
              helperText="Friendly name displayed to peers"
              inputProps={{ maxLength: 256 }}
            />

            <TextField
              fullWidth
              label="Support Email"
              type="email"
              value={formData.supportEmail}
              onChange={(e) => setFormData({ ...formData, supportEmail: e.target.value })}
              margin="normal"
              helperText="Mailbox used for escalations"
              inputProps={{ maxLength: 320 }}
              InputProps={{
                startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />

            <TextField
              fullWidth
              label="Support Phone Number"
              value={formData.supportPhoneNumber}
              onChange={(e) => setFormData({ ...formData, supportPhoneNumber: e.target.value })}
              margin="normal"
              helperText="Fallback voice/SMS number"
              inputProps={{ maxLength: 32 }}
              InputProps={{
                startAdornment: <Phone sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />

            <Box mt={2} mb={1}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Support Modes
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Select the contact modes you prefer
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {SUPPORT_MODES.map((mode) => {
                  const Icon = mode.icon
                  const isSelected = formData.supportMode?.includes(mode.value)
                  return (
                    <FormControlLabel
                      key={mode.value}
                      control={
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleModeToggle(mode.value)}
                          color="primary"
                        />
                      }
                      label={
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Icon fontSize="small" />
                          <span>{mode.label}</span>
                        </Box>
                      }
                    />
                  )
                })}
              </Box>
            </Box>
          </>
        ) : (
          <>
            <InfoRow>
              <InfoLabel>Screen Name</InfoLabel>
              <InfoValue component="div">{profile?.screenName || '--'}</InfoValue>
            </InfoRow>

            <InfoRow>
              <InfoLabel>Support Email</InfoLabel>
              <InfoValue component="div">
                {profile?.supportEmail ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <Email fontSize="small" color="action" />
                    {profile.supportEmail}
                  </Box>
                ) : (
                  '--'
                )}
              </InfoValue>
            </InfoRow>

            <InfoRow>
              <InfoLabel>Support Phone</InfoLabel>
              <InfoValue component="div">
                {profile?.supportPhoneNumber ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <Phone fontSize="small" color="action" />
                    {profile.supportPhoneNumber}
                  </Box>
                ) : (
                  '--'
                )}
              </InfoValue>
            </InfoRow>

            <InfoRow>
              <InfoLabel>Support Modes</InfoLabel>
              <InfoValue component="div">
                {profile?.supportMode && profile.supportMode.length > 0 ? (
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {profile.supportMode.map((mode) => {
                      const modeConfig = SUPPORT_MODES.find((m) => m.value === mode)
                      const Icon = modeConfig?.icon || Phone
                      return (
                        <ModeChip
                          key={mode}
                          icon={<Icon fontSize="small" />}
                          label={modeConfig?.label || mode}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      )
                    })}
                  </Box>
                ) : (
                  '--'
                )}
              </InfoValue>
            </InfoRow>
          </>
        )}
      </Box>
    </ProfileContainer>
  )
}

